<template>
  <div class="comment-item" :class="{ 'is-reply': depth > 0 }">
    <div class="comment-main">
      <img :src="userAvatar" :alt="comment.user_name" class="comment-avatar" />

      <div class="comment-content-wrapper">
        <div class="comment-bubble">
          <span class="comment-author">{{ comment.user_name }}</span>
          <span v-if="comment.user_type !== 'User'" class="author-badge">
            {{ comment.user_type }}
          </span>

          <!-- Edit Mode -->
          <div v-if="isEditing" class="edit-mode">
            <textarea
              v-model="editedContent"
              class="edit-textarea"
              @keydown.enter.exact.prevent="saveEdit"
              @keydown.esc="cancelEdit"
            ></textarea>
            <div class="edit-actions">
              <button class="cancel-btn" @click="cancelEdit">Cancel</button>
              <button class="save-btn" @click="saveEdit">Save</button>
            </div>
          </div>

          <!-- Display Mode -->
          <p v-else class="comment-text">{{ comment.content }}</p>
        </div>

        <!-- Comment Actions -->
        <div class="comment-actions">
          <span class="comment-time">{{ formatTime(comment.created_at) }}</span>

          <!-- Reaction Button -->
          <button
            class="action-link"
            :class="{ reacted: hasUserReacted }"
            @click="toggleReaction"
          >
            {{ hasUserReacted ? userReactionEmoji : 'Like' }}
          </button>

          <!-- Reply Button (only if depth < 3) -->
          <button
            v-if="depth < 3"
            class="action-link"
            @click="showReplyInput = !showReplyInput"
          >
            Reply
          </button>

          <!-- Edit/Delete for own comments -->
          <template v-if="isOwnComment">
            <button class="action-link" @click="startEdit">Edit</button>
            <button class="action-link delete" @click="deleteComment">
              Delete
            </button>
          </template>
        </div>

        <!-- Reactions Display -->
        <div v-if="hasReactions" class="reactions-display">
          <span
            v-for="(users, emoji) in comment.reactions"
            :key="emoji"
            class="reaction-item"
            :class="{ 'user-reacted': users.includes(String(userData.id)) }"
            @click="toggleSpecificReaction(emoji)"
          >
            {{ emoji }} {{ users.length }}
          </span>
        </div>

        <!-- Reply Input -->
        <CommentInput
          v-if="showReplyInput"
          :user-data="userData"
          :post-id="postId"
          :parent-comment-id="comment.id"
          :compact="true"
          :auto-focus="true"
          @comment-submitted="handleReplySubmitted"
          @cancel="showReplyInput = false"
        />
      </div>
    </div>

    <!-- Nested Replies -->
    <div v-if="comment.replies && comment.replies.length > 0" class="replies">
      <CommentItem
        v-for="reply in visibleReplies"
        :key="reply.id"
        :comment="reply"
        :user-data="userData"
        :post-id="postId"
        :depth="depth + 1"
        @reply-submitted="$emit('reply-submitted', $event)"
        @comment-deleted="$emit('comment-deleted', $event)"
        @reaction-toggled="$emit('reaction-toggled', $event)"
      />

      <button
        v-if="hasMoreReplies"
        class="show-more-replies"
        @click="showAllReplies = true"
      >
        View {{ comment.replies.length - maxVisibleReplies }} more
        {{ comment.replies.length - maxVisibleReplies === 1 ? 'reply' : 'replies' }}
      </button>
    </div>
  </div>
</template>

<script>
import { PostComments } from "@/services/team-posts-service";
import CommentInput from "./CommentInput.vue";
import { avatarUrl } from "@/utils/avatar";

export default {
  name: "CommentItem",
  components: {
    CommentInput,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    userData: {
      type: Object,
      required: true,
    },
    postId: {
      type: [Number, String],
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
  },
  emits: ["reply-submitted", "comment-deleted", "reaction-toggled"],
  data() {
    return {
      showReplyInput: false,
      isEditing: false,
      editedContent: "",
      showAllReplies: false,
      maxVisibleReplies: 2,
    };
  },
  computed: {
    userAvatar() {
      if (this.comment.user_image) {
        return this.getImgUrl(this.comment.user_image);
      }
      return this.getImgUrl("default-image.png");
    },
    isOwnComment() {
      return String(this.comment.user_id) === String(this.userData.id);
    },
    hasReactions() {
      if (!this.comment.reactions) return false;
      return Object.values(this.comment.reactions).some(
        (users) => Array.isArray(users) && users.length > 0
      );
    },
    hasUserReacted() {
      return !!this.userReactionEmoji;
    },
    userReactionEmoji() {
      if (!this.comment.reactions) return null;
      const userId = String(this.userData.id);
      for (const [emoji, users] of Object.entries(this.comment.reactions)) {
        if (Array.isArray(users) && users.map(String).includes(userId)) {
          return emoji;
        }
      }
      return null;
    },
    visibleReplies() {
      if (!this.comment.replies) return [];
      if (this.showAllReplies) return this.comment.replies;
      return this.comment.replies.slice(0, this.maxVisibleReplies);
    },
    hasMoreReplies() {
      return (
        this.comment.replies &&
        this.comment.replies.length > this.maxVisibleReplies &&
        !this.showAllReplies
      );
    },
  },
  methods: {
    getImgUrl(imageName) {
      return avatarUrl(imageName);
    },

    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;

      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
    },

    async toggleReaction() {
      const emoji = this.hasUserReacted ? this.userReactionEmoji : "👍";
      await this.toggleSpecificReaction(emoji);
    },

    async toggleSpecificReaction(emoji) {
      try {
        const response = await PostComments.toggleCommentReaction(
          this.comment.id,
          emoji,
          this.userData.id
        );

        if (response.data.success) {
          this.$emit("reaction-toggled", {
            commentId: this.comment.id,
            reactions: response.data.reactions,
          });
        }
      } catch (error) {
        console.error("Error toggling reaction:", error);
      }
    },

    startEdit() {
      this.editedContent = this.comment.content;
      this.isEditing = true;
    },

    cancelEdit() {
      this.isEditing = false;
      this.editedContent = "";
    },

    async saveEdit() {
      if (!this.editedContent.trim()) return;

      try {
        const response = await PostComments.updateComment(
          this.comment.id,
          this.editedContent,
          this.userData.id
        );

        if (response.data.success) {
          this.comment.content = this.editedContent;
          this.comment.updated_at = new Date().toISOString();
          this.isEditing = false;
        }
      } catch (error) {
        console.error("Error updating comment:", error);
        alert("Failed to update comment");
      }
    },

    async deleteComment() {
      if (!confirm("Delete this comment?")) return;

      try {
        const response = await PostComments.deleteComment(this.comment.id);
        if (response.data.success) {
          this.$emit("comment-deleted", this.comment.id);
        }
      } catch (error) {
        console.error("Error deleting comment:", error);
        alert("Failed to delete comment");
      }
    },

    handleReplySubmitted(newReply) {
      this.showReplyInput = false;
      this.$emit("reply-submitted", {
        parentComment: this.comment,
        newReply,
      });
    },
  },
};
</script>

<style scoped>
.comment-item {
  margin-bottom: 8px;
}

.comment-item.is-reply {
  margin-left: 44px;
}

.comment-main {
  display: flex;
  gap: 8px;
}

.comment-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.is-reply .comment-avatar {
  width: 28px;
  height: 28px;
}

.comment-content-wrapper {
  flex: 1;
  min-width: 0;
}

.comment-bubble {
  display: inline-block;
  background: #f0f2f5;
  border-radius: 18px;
  padding: 8px 12px;
  max-width: 100%;
}

.comment-author {
  font-weight: 600;
  font-size: 13px;
  color: #050505;
  margin-right: 4px;
}

.author-badge {
  font-size: 11px;
  color: #65676b;
  background: #e4e6eb;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 4px;
}

.comment-text {
  font-size: 15px;
  color: #050505;
  margin: 0;
  word-wrap: break-word;
}

.edit-mode {
  margin-top: 4px;
}

.edit-textarea {
  width: 100%;
  min-height: 60px;
  padding: 8px;
  border: 1px solid #dddfe2;
  border-radius: 8px;
  font-size: 15px;
  resize: none;
  outline: none;
}

.edit-textarea:focus {
  border-color: #1877f2;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.cancel-btn,
.save-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.cancel-btn {
  background: #e4e6eb;
  color: #050505;
}

.save-btn {
  background: #1877f2;
  color: #fff;
}

.comment-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 12px;
  font-size: 12px;
}

.comment-time {
  color: #65676b;
}

.action-link {
  background: none;
  border: none;
  color: #65676b;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
}

.action-link:hover {
  text-decoration: underline;
}

.action-link.reacted {
  color: #1877f2;
}

.action-link.delete {
  color: #e41e3f;
}

.reactions-display {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 12px;
}

.reaction-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  background: #f0f2f5;
  border-radius: 10px;
  font-size: 12px;
  cursor: pointer;
}

.reaction-item:hover {
  background: #e4e6eb;
}

.reaction-item.user-reacted {
  background: #e7f3ff;
  border: 1px solid #1877f2;
}

.replies {
  margin-top: 4px;
}

.show-more-replies {
  background: none;
  border: none;
  color: #65676b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0 8px 44px;
}

.show-more-replies:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .comment-item.is-reply {
    margin-left: 32px;
  }

  .comment-bubble {
    border-radius: 12px;
  }
}
</style>
